import React, { useMemo } from 'react';

import * as Icons from '@tabler/icons-react';
import { GatsbyImage, ImageDataLike, getImage } from 'gatsby-plugin-image';

import { Button } from '../button';

interface CtaEntryProps {
  className?: string;
  title: string;
  body: string;
  link: string;
  buttonText: string;
  primaryColorLight?: string;
  primaryColorDark?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  iconName?: Exclude<keyof typeof Icons, 'createReactComponent'>;
  imageData?: ImageDataLike;
  imageUrl?: string;
  bottomPadding?: boolean;
}

const CtaEntry = (props: CtaEntryProps) => {
  const { title, body, buttonText, link, imageData, imageUrl } = props;

  const Image = useMemo(() => {
    if (imageData) {
      const gatsbyImageData = getImage(imageData);
      if (gatsbyImageData) {
        return (
          <GatsbyImage
            style={{
              width: '100%',
              height: '100%',
            }}
            imgStyle={{
              width: '100%',
              height: '100%',
            }}
            image={gatsbyImageData}
            objectFit={props.bottomPadding ? 'contain' : 'cover'}
            objectPosition="top"
            alt=""
          />
        );
      }
      return null;
    }
    return (
      <img
        style={props.bottomPadding ? { objectFit: 'contain' } : undefined}
        src={imageUrl}
        alt=""
        role="presentation"
      />
    );
  }, [imageData, imageUrl, props.bottomPadding]);

  return (
    <div
      style={{ background: props.primaryColorLight }}
      className={[
        classNames.container,
        !props.primaryColorLight ? 'tw-bg-mauve-100' : '',
        props.bottomPadding ? classNames.contentBottomPadding : '',
        props.className,
      ].join(' ')}
    >
      <div className={classNames.content}>
        <div className={classNames.textContainer}>
          <div className="tw-h3-emphasis">{title}</div>
          <div className="tw-body1 tw-mt-[8px]">{body}</div>
          <a
            className="tw-flex tw-max-w-max tw-mt-[24px] tw-mb-[24px] tw-text-transparent tw-decoration-0"
            href={link}
            target="_blank"
            rel="noreferrer"
          >
            <Button
              style={{ color: props.primaryColorDark }}
              className={[
                classNames.button,
                !props.primaryColorDark ? 'tw-text-mauve-700' : '',
              ].join(' ')}
              preset="noBackground"
              text={buttonText}
              onClick={props.onClick}
            />
          </a>
        </div>
        <div className={classNames.imageContainer}>{Image}</div>
      </div>
    </div>
  );
};

const classNames = {
  container: [
    'tw-flex',
    'tw-h-[475px]',
    'tw-pt-[24px]',
    'tw-px-[16px]',
    'md:tw-h-[350px]',
    'md:tw-pt-[40px]',
    'md:tw-px-[40px]',
    'tw-overflow-hidden',
  ].join(' '),
  content: [
    'tw-grid',
    'tw-grid-cols-[100%]',
    'tw-grid-rows-[auto,max(50%)]',
    'md:tw-grid-cols-[50%,50%]',
    'md:tw-grid-rows-[100%]',
  ].join(' '),
  textContainer: ['tw-flex', 'tw-flex-col'].join(' '),
  contentBottomPadding: ['tw-pb-[24px]', 'md:tw-pb-[40px]'].join(' '),
  button: ['tw-self-start'].join(' '),
  imageContainer: ['tw-flex', 'md:tw-ml-[32px]'].join(' '),
};

export { CtaEntry };
